
const CHUNK_RELOAD_KEY = "upsider_chunk_reload";

export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const name = typeof err === "object" && "name" in err ? String((err as { name?: unknown }).name) : "";
  if (name === "ChunkLoadError") return true;
  const message =
    typeof err === "object" && "message" in err
      ? String((err as { message?: unknown }).message)
      : String(err);
  return (
    /Loading (?:CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

const UNKNOWN_DEPLOYMENT = "unknown";

function currentDeploymentId(): string {
  return process.env.NEXT_DEPLOYMENT_ID || UNKNOWN_DEPLOYMENT;
}

export function chunkReloadStorage(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function canReloadForChunkError(err: unknown, storage: Pick<Storage, "getItem"> | null): boolean {
  if (!storage || !isChunkLoadError(err)) return false;
  try {
    return storage.getItem(CHUNK_RELOAD_KEY) !== currentDeploymentId();
  } catch {
    return false;
  }
}

export function reloadForChunkError(storage: Pick<Storage, "setItem"> | null, reload: () => void): boolean {
  if (!storage) return false;
  try {
    storage.setItem(CHUNK_RELOAD_KEY, currentDeploymentId());
  } catch {
    return false;
  }
  reload();
  return true;
}

export function handleChunkLoadError(
  err: unknown,
  storage: Pick<Storage, "getItem" | "setItem"> | null,
  reload: () => void,
): boolean {
  return canReloadForChunkError(err, storage) && reloadForChunkError(storage, reload);
}

export function installChunkReloadHandler(): void {
  if (typeof window === "undefined") return;
  const onError = (event: ErrorEvent) => {
    handleChunkLoadError(event.error, chunkReloadStorage(), () => window.location.reload());
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    handleChunkLoadError(event.reason, chunkReloadStorage(), () => window.location.reload());
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
}
